import mainBackground from "@/assets/images/mailvista-background.webp";
import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

interface BackgroundPreloaderContextType {
    isBackgroundLoaded: boolean;
    hasBackgroundError: boolean;
    backgroundImageUrl: string;
}

const BackgroundPreloaderContext =
    createContext<BackgroundPreloaderContextType>({
        isBackgroundLoaded: false,
        hasBackgroundError: false,
        backgroundImageUrl: mainBackground,
    });

export const useBackgroundPreloader = () => {
    const context = useContext(BackgroundPreloaderContext);
    if (!context) {
        throw new Error(
            "useBackgroundPreloader must be used within a BackgroundPreloaderProvider",
        );
    }

    return context;
};

interface BackgroundPreloaderProviderProps {
    children: React.ReactNode;
}

export const BackgroundPreloaderProvider = ({
    children,
}: BackgroundPreloaderProviderProps) => {
    const [isBackgroundLoaded, setIsBackgroundLoaded] = useState(false);
    const [hasBackgroundError, setHasBackgroundError] = useState(false);

    useEffect(() => {
        const img = new Image();

        img.onload = () => {
            setIsBackgroundLoaded(true);
            setHasBackgroundError(false);
        };

        img.onerror = () => {
            setHasBackgroundError(true);
            setIsBackgroundLoaded(true);
        };

        img.src = mainBackground;

        return () => {
            img.onload = null;
            img.onerror = null;
        };
    }, []);

    const contextValue = useMemo(
        () => ({
            isBackgroundLoaded,
            hasBackgroundError,
            backgroundImageUrl: mainBackground,
        }),
        [isBackgroundLoaded, hasBackgroundError],
    );

    return (
        <BackgroundPreloaderContext.Provider value={contextValue}>
            {children}
        </BackgroundPreloaderContext.Provider>
    );
};
